/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IDobbiesStoreBasedNotificationsData } from './dobbies-store-based-notifications.data';
import { IDobbiesStoreBasedNotificationsProps, searchRadiusUnit } from './dobbies-store-based-notifications.props.autogenerated';
import { AttributeDefinition, CustomerAttribute } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    updateCustomerAttributes,
    updateCustomerAttributesInput
} from '@msdyn365-commerce-modules/retail-actions';
import { Modal, ModalBody, ModalHeader, Button } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { getDateObjFromString } from '../../themes/dobbies/utilities/formatDate';
import { getVariantsByDimensionValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
// import * as Msdyn365 from '@msdyn365-commerce/core';

export interface IDobbiesStoreBasedNotificationsViewProps
    extends IDobbiesStoreBasedNotificationsProps<IDobbiesStoreBasedNotificationsData> {
    notificationModal?: JSX.Element;
    storeLocationsModal?: JSX.Element;
    state: IDobbiesStoreBasedNotificationsState;
}

/**
 *
 * DobbiesStoreBasedNotifications component
 * @extends {React.PureComponent<IDobbiesStoreBasedNotificationsProps<IDobbiesStoreBasedNotificationsData>>}
 */

// interface INotification {
//     storeName: string;
//     notificationBannerImage: string;
//     notificationHeading: string;
//     notificationType: string;
//     notificationContent: Msdyn365.RichText;
//     notificationFooter: Msdyn365.RichText;
//     notificationStartDate?: string;
//     notificationShowEndDate?: string;
// }
export interface IDobbiesStoreBasedNotificationsState {
    notificationData: any;
    isLocationDisabled: boolean;
    isNotificationModal: boolean;
    localStore: object | any;
    isStoreLocationModalOpen: boolean;
    nearestGeoBasedStore: string;
}

export interface IDobbiesNumOfNotificationsToUser {
    userEmail?: string;
    numberOfNotificationsShown: number;
}

export interface IDobbiesNumberOfNotificationsShownList {
    numOfTimesNotificationsShownList: IDobbiesNumOfNotificationsToUser[];
}

class DobbiesStoreBasedNotifications extends React.PureComponent<
    IDobbiesStoreBasedNotificationsProps<IDobbiesStoreBasedNotificationsData>,
    IDobbiesStoreBasedNotificationsState
> {
    constructor(props: IDobbiesStoreBasedNotificationsProps<IDobbiesStoreBasedNotificationsData>) {
        super(props);
        this.state = {
            isLocationDisabled: false,
            notificationData: {
                storeName: '',
                notificationBannerImage: '',
                notificationHeading: '',
                notificationType: '',
                notificationFooter: '',
                notificationStartDate: '',
                notificationShowEndDate: '',
                notificationContent: ''
            },
            isNotificationModal: false,
            localStore: {},
            isStoreLocationModalOpen: false,
            nearestGeoBasedStore: ''
        };

        this.authenticateUser();
    }

    private authenticateUser = () => {
        const {
            data: {
                customerInformation: { result }
            },
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            }
        } = this.props;

        const isLoggedIn = result && result.Name ? true : false;
        if (isLoggedIn === false) {
            cookies.remove('store_location_popup');
            cookies.get('notification_popup').value === 'signedin' && cookies.remove('notification_popup');
        } else {
            cookies.get('notification_popup').value === 'signedout' && cookies.remove('notification_popup');
        }
    };

    // Render dropdown options for localstore attribute
    private renderLocalStores = () => {
        const localStoreAttr = this.props.data?.attributeDefinitions?.result?.find(
            x => x.AttributeTypeName?.toLowerCase() === 'local store'
        );
        const localStores = localStoreAttr?.EnumerationDetails?.sort((a, b) => a.EnumerationValue!.localeCompare(b.EnumerationValue!));
        // Move none value to the start of array
        const noneVal =
            localStores &&
            localStores.find(store => {
                return store.EnumerationValue && store.EnumerationValue.toLowerCase() === 'none';
            });
        localStores?.sort((x, y) => {
            return x.EnumerationValue === noneVal?.EnumerationValue ? -1 : y === noneVal?.EnumerationValue ? 1 : 0;
        });
        return localStores?.map(e => {
            return (
                <option aria-selected='true' value={e.EnumerationValue} key={e.EnumerationValue}>
                    {e.EnumerationValue}
                </option>
            );
        });
    };

    private geoBasedNotification = async () => {
        const {
            config: { urlsToHideStoreBasednotifications, latitude, longitude }
        } = this.props;

        if (
            urlsToHideStoreBasednotifications &&
            urlsToHideStoreBasednotifications.includes(this.props.context.request.url.requestUrl.href)
        ) {
            return;
        }

        if (!navigator?.geolocation) {
            this.preferredStoreNotification();
            return;
        }

        // Try using cached location first
        const cachedLocationString = localStorage.getItem('userLocation');
        let cachedLocation: { lat: number; lng: number; timestamp?: number } | null = null;
        const cacheHours = this.props.context.app.config.userLocationCacheTime || 12;
        const CACHE_HOURS_MS = cacheHours * 60 * 60 * 1000;

        try {
            cachedLocation = cachedLocationString ? JSON.parse(cachedLocationString) : null;
        } catch {
            localStorage.removeItem('userLocation');
        }

        // Check if cached location exists and is not older than 12 hours
        const isCacheValid = cachedLocation && cachedLocation.timestamp && Date.now() - cachedLocation.timestamp < CACHE_HOURS_MS;

        // If cached location exists and is valid, use it
        if (cachedLocation && isCacheValid) {
            this.setState({ isLocationDisabled: false });
            await this._updateLocations(cachedLocation.lat, cachedLocation.lng);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async position => {
                const userCoords = position.coords;

                const coords =
                    latitude !== 0 && longitude !== 0
                        ? { latitude, longitude }
                        : {
                              latitude: userCoords.latitude,
                              longitude: userCoords.longitude
                          };

                // Cache with timestamp
                localStorage.setItem(
                    'userLocation',
                    JSON.stringify({
                        lat: userCoords.latitude,
                        lng: userCoords.longitude,
                        timestamp: Date.now()
                    })
                );

                this.setState({ isLocationDisabled: false });

                await this._updateLocations(coords.latitude, coords.longitude);
            },
            error => {
                console.log('Geolocation error:', error);
                this.preferredStoreNotification();
            },
            {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: Infinity
            }
        );
    };

    private _updateLocations = async (latitude?: number, longitude?: number): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            }
        } = this.props;
        const { storeBasedNotifications, lookupRadius } = this.props.config;
        const calRadius = lookupRadius ? lookupRadius : 0;
        const distanceUnitValue = this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? 1 : 0;
        const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(
            latitude,
            longitude,
            calRadius,
            distanceUnitValue,
            this.state.isLocationDisabled
        );

        const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, this.props.context.actionContext);
        if (orgUnitLocations && orgUnitLocations?.length > 0) {
            const tempSortedLocations = orgUnitLocations.sort(
                (a, b) => a.OrgUnitAvailability!.OrgUnitLocation!.Distance! - b.OrgUnitAvailability!.OrgUnitLocation!.Distance!
            );
            const cookienotificationValue = cookies.get('notification_popup');
            if (tempSortedLocations.length > 0 && storeBasedNotifications!.length > 0) {
                const { OrgUnitAvailability } = tempSortedLocations[0];
                this.setState({ nearestGeoBasedStore: OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName?.toLocaleLowerCase()! });
                storeBasedNotifications?.map((notify: any) => {
                    const { storeName, notificationStartDate, notificationShowEndDate } = notify;
                    if (
                        cookienotificationValue.value === undefined &&
                        storeName.toLocaleLowerCase().trim() === OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName?.toLocaleLowerCase() &&
                        this.checkNotificationTime(notificationStartDate, notificationShowEndDate) &&
                        notify.notificationType
                    ) {
                        if (notify?.activePoints && notify?.activePoints?.length > 0) {
                            this.setCookie('notification_products', JSON.stringify(notify?.activePoints), 1);
                            this.getProductsListWithKitProductsHandled(
                                notify?.activePoints.map((ap: { itemId: string | number }) => Number(ap.itemId))
                            )
                                .then(prodIds => {
                                    const handledProdIds = prodIds.map(pid => {
                                        return { itemId: pid };
                                    });
                                    this.setCookie('notification_products_for_summary', JSON.stringify(handledProdIds), 1);
                                })
                                .catch(e => {});
                        }
                        this.setState({ notificationData: notify, isNotificationModal: true });
                    }
                });

                // show stores selection popup
                const isStoreExist = storeBasedNotifications?.find(
                    notification =>
                        notification.storeName.toLocaleLowerCase().trim() ===
                        OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName?.toLocaleLowerCase()
                );
                if (
                    !this.state.isNotificationModal &&
                    cookienotificationValue.value === undefined &&
                    (!isStoreExist ||
                        (isStoreExist &&
                            (!this.checkNotificationTime(isStoreExist.notificationStartDate, isStoreExist.notificationShowEndDate) ||
                                !isStoreExist.notificationType)))
                ) {
                    this.preferredStoreNotification();
                }
            }
        } else {
            this.preferredStoreNotification();
        }
    };

    private preferredStoreNotification = () => {
        const {
            data: { attributeDefinitions, customerInformation },
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            }
        } = this.props;

        const customer = customerInformation.result!;
        const attributeDefinition: AttributeDefinition[] = (attributeDefinitions && attributeDefinitions.result) || [];
        const foundObject = customer && customer.Attributes?.find(item => item.Name === 'Local Store');
        const foundAttrInDef = attributeDefinition?.find(item => item.Name === 'Local Store');
        const cookieStoreValue = cookies.get('store_location_popup');
        if (
            (!foundObject && this.props.context.request.user.isAuthenticated) ||
            (foundObject &&
                foundObject.AttributeValue?.StringValue === 'None' &&
                foundAttrInDef &&
                cookieStoreValue &&
                cookieStoreValue.value === undefined)
        ) {
            this.setState({ localStore: this.setAttributeValue(foundAttrInDef?.DefaultValue?.StringValue, foundAttrInDef, false) });
            this.setState({ isStoreLocationModalOpen: true });
        }

        // store based notifications
        const cookienotificationValue = cookies.get('notification_popup');
        const storeBasedNotifications = this.props.config.storeBasedNotifications;
        let notificationHandled = false;
        if (
            !this.state.isStoreLocationModalOpen &&
            storeBasedNotifications &&
            storeBasedNotifications.length > 0 &&
            foundObject &&
            foundObject.AttributeValue?.StringValue !== 'None'
        ) {
            storeBasedNotifications.map((str: any) => {
                const { storeName, notificationStartDate, notificationShowEndDate } = str;
                if (
                    cookienotificationValue.value === undefined &&
                    storeName.toLocaleLowerCase().trim() === foundObject.AttributeValue?.StringValue?.toLocaleLowerCase() &&
                    this.checkNotificationTime(notificationStartDate, notificationShowEndDate) &&
                    str.notificationType &&
                    !this.state.isNotificationModal
                ) {
                    if (str?.activePoints && str?.activePoints?.length > 0) {
                        this.setCookie('notification_products', JSON.stringify(str?.activePoints), 1);
                        this.getProductsListWithKitProductsHandled(
                            str?.activePoints.map((ap: { itemId: string | number }) => Number(ap.itemId))
                        )
                            .then(prodIds => {
                                const handledProdIds = prodIds.map(pid => {
                                    return { itemId: pid };
                                });
                                this.setCookie('notification_products_for_summary', JSON.stringify(handledProdIds), 1);
                            })
                            .catch(e => {});
                    }
                    notificationHandled = true;
                    this.setState({ notificationData: str, isNotificationModal: true });
                }
            });
        }
        // show notification based on country grouping
        if (!notificationHandled && !this.state.isNotificationModal && !this.state.isStoreLocationModalOpen) {
            this.countryBasedNotification();
        }
    };

    private countryBasedNotification = () => {
        const {
            allEnglandStores,
            allScotlandStores,
            allNorthernIrelandStores,
            allWalesStores,
            storeBasedNotifications
        } = this.props.config;

        const { isStoreLocationModalOpen, nearestGeoBasedStore } = this.state;
        const {
            data: { customerInformation }
        } = this.props;
        const customer = customerInformation.result!;
        const { user } = this.props.context.request;
        const { cookies } = this.props.context.actionContext.requestContext;
        const foundObject = customer && customer.Attributes?.find(item => item.Name === 'Local Store');
        const foundStoreName = foundObject?.AttributeValue?.StringValue?.toLowerCase() ?? null;
        const cookieStoreValue = cookies.get('store_location_popup');
        if (
            !isStoreLocationModalOpen &&
            ((user.isAuthenticated && foundStoreName === 'none' && cookieStoreValue.value !== undefined) ||
                (user.isAuthenticated && storeBasedNotifications?.some(notify => notify.storeName !== foundStoreName)) ||
                (!user.isAuthenticated && !foundObject && cookieStoreValue.value === undefined && storeBasedNotifications?.length! > 0))
        ) {
            const checkStoreAndNotify = (stores: string[], regionName: string) => {
                const regionStores = stores?.map(store => store.toLowerCase().trim()) ?? [];
                const isStoreFound =
                    regionStores.includes(nearestGeoBasedStore?.toLowerCase()) || regionStores.includes(foundStoreName ?? '');

                if (!isStoreFound) {
                    return false;
                }

                const notification = storeBasedNotifications?.find(notify => notify.storeName.toLowerCase().trim() === regionName);

                if (!notification) {
                    return false;
                }

                const { notificationStartDate, notificationShowEndDate, activePoints } = notification;
                const points = activePoints as ({ itemId: string | number } | undefined)[];
                const cookieValue = cookies.get('notification_popup')?.value;

                if (
                    cookieValue === undefined &&
                    this.checkNotificationTime(notificationStartDate, notificationShowEndDate) &&
                    notification.notificationType
                ) {
                    if (points && points?.length > 0) {
                        this.setCookie('notification_products', JSON.stringify(points), 1);
                        this.getProductsListWithKitProductsHandled(points.map(ap => Number(ap!.itemId)))
                            .then(prodIds => {
                                const handledProdIds = prodIds.map(pid => ({ itemId: pid }));
                                this.setCookie('notification_products_for_summary', JSON.stringify(handledProdIds), 1);
                            })
                            .catch(() => {});
                    }
                    this.setState({ notificationData: notification, isNotificationModal: true });
                    return true;
                } else {
                    return false;
                }
            };

            // Check notifications for specific regions
            if (checkStoreAndNotify(allEnglandStores!, 'all england stores')) {
                return;
            }
            if (checkStoreAndNotify(allScotlandStores!, 'all scotland stores')) {
                return;
            }
            if (checkStoreAndNotify(allNorthernIrelandStores!, 'all northern ireland stores')) {
                return;
            }
            if (checkStoreAndNotify(allWalesStores!, 'all wales stores')) {
                return;
            }

            // Default to all stores notification
            const allStoresNotify = storeBasedNotifications?.find(notify => notify.storeName.toLowerCase().trim() === 'all stores');

            if (allStoresNotify) {
                const { notificationStartDate, notificationShowEndDate, activePoints } = allStoresNotify;
                const points = activePoints as ({ itemId: string | number } | undefined)[];
                const cookieValue = cookies.get('notification_popup')?.value;

                if (
                    cookieValue === undefined &&
                    this.checkNotificationTime(notificationStartDate, notificationShowEndDate) &&
                    allStoresNotify.notificationType
                ) {
                    if (points && points.length > 0) {
                        this.setCookie('notification_products', JSON.stringify(points), 1);
                        this.getProductsListWithKitProductsHandled(points.map(ap => Number(ap!.itemId)))
                            .then(prodIds => {
                                const handledProdIds = prodIds.map(pid => ({ itemId: pid }));
                                this.setCookie('notification_products_for_summary', JSON.stringify(handledProdIds), 1);
                            })
                            .catch(() => {});
                    }
                    this.setState({ notificationData: allStoresNotify, isNotificationModal: true });
                }
            }
        }
    };

    private async getProductsListWithKitProductsHandled(products: number[]): Promise<number[]> {
        const finalProducts: number[] = [];
        if (products && products.length) {
            for (const p of products) {
                if (p) {
                    const variants = await getVariantsByDimensionValuesAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        p,
                        this.props.context.request.apiSettings.channelId,
                        []
                    );
                    if (variants && variants.length === 1) {
                        // products = products.filter(p => p.RecordId !== p.RecordId);
                        finalProducts.push(variants[0].RecordId);
                    } else {
                        finalProducts.push(p);
                    }
                }
            }
        }
        return finalProducts;
    }

    private setAttributeValue = (value: string | boolean | undefined, attribute: CustomerAttribute | undefined, isBoolean: boolean) => {
        return {
            Attribute: 0,
            DataTypeValue: attribute!.DataTypeValue,
            KeyName: attribute!.Name,
            Name: attribute!.Name,
            RecordId: attribute!.RecordId,
            Units: '',
            AttributeValue: {
                StringValue: value
            },
            ExtensionProperties: [],
            NameTranslations: []
        };
    };

    private changeDateFormat = (date: string) => {
        const dateTimeSplittedDate = date.split(' ');
        const startDateMonth = dateTimeSplittedDate[0].split('-')[1];
        const startDateDay = dateTimeSplittedDate[0].split('-')[0];
        const startDateYear = dateTimeSplittedDate[0].split('-')[2];

        const startDateHour = dateTimeSplittedDate[1].split(':')[0];
        const startDateMinutes = dateTimeSplittedDate[1].split(':')[1];

        const changeToDDMMYY = `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00`;
        const notificationShowingDate = new Date(
            getDateObjFromString(changeToDDMMYY, changeToDDMMYY.includes('-') ? '-' : '/', changeToDDMMYY.includes(':'))
        );
        return notificationShowingDate;
    };
    private checkNotificationTime = (start: string | undefined, end: string | undefined) => {
        const currentDate = new Date();
        const changedStartDateFormat = start !== undefined && start !== '' ? this.changeDateFormat(start) : '';
        const changedEndDateFormat = end !== undefined && end !== '' ? this.changeDateFormat(end) : '';
        if (!changedStartDateFormat && !changedEndDateFormat) {
            // Show notification continuously
            return true;
        } else if (changedStartDateFormat && !changedEndDateFormat) {
            // Show notification from start date
            const startDate = new Date(changedStartDateFormat);
            return currentDate >= startDate;
        } else if (!changedStartDateFormat && changedEndDateFormat) {
            // Show notification till end date
            const endDate = new Date(changedEndDateFormat);
            return currentDate <= endDate;
        } else if (changedStartDateFormat && changedEndDateFormat) {
            // Show notification in between start and end dates
            const startDate = new Date(changedStartDateFormat);
            const endDate = new Date(changedEndDateFormat);
            return currentDate >= startDate && currentDate <= endDate;
        }
        return false;
    };
    private setCookie = (name: string, value: string, deltaDaysFromToday: number) => {
        const { cookies } = this.props.context.request;
        const expirationDate = new Date(Date.now());
        expirationDate.setTime(expirationDate.getTime() + deltaDaysFromToday * 24 * 60 * 60 * 1000);

        cookies.set(name, value, {
            expires: deltaDaysFromToday ? expirationDate : null,
            isEssential: true
        } as {});
    };
    private onCloseNotificatioModal = () => {
        const notifiCheck = this.props.context.request.user.isAuthenticated ? 'signedin' : 'signedout';
        this.setCookie('notification_popup', notifiCheck, 1);
        this.props.context.request.cookies.remove('notification_added_products');
        this.props.context.request.cookies.remove('notification_products_prices');
        this.setState({ isNotificationModal: false });
    };

    private onCloseStoreLocationModal = () => {
        interface customerIn {
            customerAccountNumber?: string;
            store: false;
        }
        const customer: customerIn = {
            customerAccountNumber: this.props.context.actionContext.requestContext.user.customerAccountNumber,
            store: false
        };
        this.setCookie('store_location_popup', JSON.stringify(customer), 1);
        this.setState({ isStoreLocationModalOpen: false });
    };
    private updateLocalStore = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedLocalStore = event.target.value;
        this.setState({ localStore: this.setAttributeValue(selectedLocalStore, this.state.localStore, false) });
    };
    private updateStoreAttr = async () => {
        const {
            data: { customerInformation }
        } = this.props;
        const customer = customerInformation.result!;
        const updatedAttr = this.updateOrAddObject(this.state.localStore, customer.Attributes || []);

        if (this.state.localStore.AttributeValue.StringValue !== 'None') {
            const input = new updateCustomerAttributesInput(
                customer.AccountNumber,
                updatedAttr || [],
                this.props.context.request.apiSettings
            );
            const cancelResponse = await updateCustomerAttributes(input, this.props.context.actionContext).catch();
            if (
                cancelResponse &&
                cancelResponse.Attributes?.filter(attr => attr.Name === 'Local Store')[0].AttributeValue!.StringValue !== 'None'
            ) {
                this.setState({ isStoreLocationModalOpen: false });
            }
        }
    };
    private updateOrAddObject(newObject: CustomerAttribute, dataArray: CustomerAttribute[]) {
        const existingObject = dataArray.find((item: any) => item.Name === newObject.Name);
        if (existingObject) {
            Object.assign(existingObject, newObject);
        } else {
            dataArray.push(newObject);
        }
        return dataArray;
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            }
        } = this.props;
        cookies.remove('notification_added_products');
        cookies.remove('notification_products_prices');
        await this.geoBasedNotification();
    }

    public componentDidUpdate(prevProps: any, prevState: { isStoreLocationModalOpen: boolean; isNotificationModal: boolean }) {
        if (prevState.isStoreLocationModalOpen !== this.state.isStoreLocationModalOpen) {
            this.preferredStoreNotification();
        }
        if (prevState.isNotificationModal !== this.state.isNotificationModal && this.state.isNotificationModal) {
            this.updateNumOfTimesNotificationsDisplayed();
        }
    }

    public render(): JSX.Element | null {
        const notificationsProps = {
            ...this.props,
            state: this.state,
            notificationModal: this.renderNotificationModal(),
            storeLocationsModal: this.renderStoreLocationModal()
        };
        return this.props.renderView(notificationsProps) as React.ReactElement;
    }

    private updateNumOfTimesNotificationsDisplayed = () => {
        const numOfTimesNotificationShown: IDobbiesNumberOfNotificationsShownList = { numOfTimesNotificationsShownList: [] };
        const numOfTimesNotificationShownCookie = this.props.context.request.cookies.get('numOfTimesNotificationShown');
        let contactFound = false;
        if (numOfTimesNotificationShownCookie) {
            const numOfTimesNotificationShownCookieValue = numOfTimesNotificationShownCookie.value;
            if (numOfTimesNotificationShownCookieValue && Array.isArray(numOfTimesNotificationShownCookieValue)) {
                for (const numOfTimesObj of numOfTimesNotificationShownCookieValue) {
                    const numTimes = numOfTimesObj['numberOfNotificationsShown'];
                    const usernameOrEmail = numOfTimesObj['userEmail'];
                    const cNumber = this.props.context.request.user.customerAccountNumber;
                    if (
                        usernameOrEmail === this.props.context.request.user.emailAddress ||
                        cNumber === usernameOrEmail ||
                        (!this.props.context.request.user.emailAddress && !cNumber && usernameOrEmail === 'default')
                    ) {
                        contactFound = true;
                        numOfTimesNotificationShown.numOfTimesNotificationsShownList.push({
                            numberOfNotificationsShown: numTimes + 1,
                            userEmail: usernameOrEmail
                        });
                    } else {
                        numOfTimesNotificationShown.numOfTimesNotificationsShownList.push({
                            numberOfNotificationsShown: numTimes,
                            userEmail: usernameOrEmail
                        });
                    }
                }
            }
        }
        if (contactFound) {
            this.props.context.request.cookies.set(
                'numOfTimesNotificationShown',
                numOfTimesNotificationShown.numOfTimesNotificationsShownList
            );
        } else {
            numOfTimesNotificationShown.numOfTimesNotificationsShownList.push({
                numberOfNotificationsShown: 1,
                userEmail:
                    this.props.context.request.user.emailAddress || this.props.context.request.user.customerAccountNumber || 'default'
            });
            this.props.context.request.cookies.set(
                'numOfTimesNotificationShown',
                numOfTimesNotificationShown.numOfTimesNotificationsShownList
            );
        }
    };

    private renderNotificationModal = (): JSX.Element => {
        // Modal Added showing Notifications
        const { notificationData } = this.state;
        if (this.state.isNotificationModal) {
            return (
                <Modal
                    autoFocus={true}
                    fade={false}
                    isOpen={this.state.isNotificationModal && !this.hasNotificationsExceededMaximumLimit()}
                    applicationNode={'renderPage'}
                    horizontalPosition={'center'}
                    verticalPosition={'center'}
                    zIndex={5003}
                    modalClassName={`notificationModal ${
                        notificationData.notificationType?.toLowerCase() === 'event' ? '' : 'notificationModal_PB'
                    }`}
                    className=''
                >
                    <ModalHeader className='p-0 border-0'>
                        {
                            <button
                                type='button'
                                className='msc-modal__close-button'
                                aria-label='Close'
                                onClick={this.onCloseNotificatioModal}
                            />
                        }
                    </ModalHeader>
                    <ModalBody className='notificationModal__body'>
                        <>
                            {this.props.slots.notificationProductsSlot && this.props.slots.notificationProductsSlot[0] && (
                                <>
                                    <div className='notificationModal_inner'>
                                        <div className='notificationModal_banner'>
                                            <h2>
                                                {notificationData.notificationBannerHeading}{' '}
                                                <span>{notificationData.notificationBannerSubHeading}</span>
                                            </h2>
                                            <img alt='' src={`${notificationData.notificationBannerImage}`} title='' />
                                        </div>
                                        <div className='notificationModal_middletext'>
                                            <h3>{notificationData.notificationHeading}</h3>
                                        </div>

                                        {notificationData.notificationType?.toLowerCase() === 'event' ? (
                                            <RichTextComponent
                                                className='notificationModal_content'
                                                text={notificationData?.notificationContent}
                                            />
                                        ) : (
                                            <div className='notificationModal_content'>
                                                <div className='product-bundle-wrapper notificationModal-product-bundle'>
                                                    <div className='product-bundle'>
                                                        <div className='product-bundle-container'>
                                                            <div className='product-bundle-left'>
                                                                {this.props.slots.notificationProductsSlot &&
                                                                    this.props.slots.notificationProductsSlot[0]}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <RichTextComponent
                                            className='notificationModal_footer'
                                            text={notificationData?.notificationFooter}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    </ModalBody>
                </Modal>
            );
        }
        return <></>;
    };

    private hasNotificationsExceededMaximumLimit = () => {
        const numOfTimesNotificationShownCookie = this.props.context.request.cookies.get('numOfTimesNotificationShown');
        if (numOfTimesNotificationShownCookie) {
            const numOfTimesNotificationShownCookieValue = numOfTimesNotificationShownCookie.value;
            if (numOfTimesNotificationShownCookieValue && Array.isArray(numOfTimesNotificationShownCookieValue)) {
                for (const numOfTimesObj of numOfTimesNotificationShownCookieValue) {
                    const numTimes = numOfTimesObj['numberOfNotificationsShown'];
                    const usernameOrEmail = numOfTimesObj['userEmail'];
                    const cNum = this.props.context.request.user.customerAccountNumber;
                    if (
                        this.props.context.request.user.emailAddress === usernameOrEmail ||
                        cNum === usernameOrEmail ||
                        (!this.props.context.request.user.emailAddress && !cNum && usernameOrEmail === 'default')
                    ) {
                        if (
                            this.props.config.maxAllowedNumberOfTimesForNotificationsToShow &&
                            numTimes >= this.props.config.maxAllowedNumberOfTimesForNotificationsToShow
                        ) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    };
    private renderStoreLocationModal = (): JSX.Element => {
        // Modal Added showing Stores
        const cookieStoreValue = this.props.context.actionContext.requestContext.cookies.get('store_location_popup');
        if (this.state.isStoreLocationModalOpen && cookieStoreValue && cookieStoreValue.value === undefined) {
            return (
                <Modal
                    autoFocus={true}
                    fade={false}
                    isOpen={this.state.isStoreLocationModalOpen}
                    applicationNode={'renderPage'}
                    horizontalPosition={'center'}
                    verticalPosition={'center'}
                    zIndex={5003}
                    modalClassName='dobbies-store-location-popup'
                    className=''
                >
                    <ModalHeader>
                        <>Select store location</>
                        {
                            <button
                                type='button'
                                className='msc-modal__close-button'
                                aria-label='Close'
                                onClick={this.onCloseStoreLocationModal}
                            />
                        }
                    </ModalHeader>
                    <ModalBody className='dobbies-club-renewal-popup__body'>
                        <div className='loyalty-signup-section__input-holder'>
                            <div>
                                <div className='ms-header__icon-store'></div>
                                <div className='select-holder'>
                                    <i className='fa fa-arrow-down'></i>
                                    <select name='store' onChange={this.updateLocalStore}>
                                        {this.renderLocalStores()}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <Button className='msc-cta store-location-apply-cta' onClick={this.updateStoreAttr}>
                                    Add
                                </Button>
                            </div>
                        </div>
                    </ModalBody>
                    {/* <ModalFooter>
                <Button className='homepopup-btn' onClick={updateStoreAttr}>Add</Button>
            </ModalFooter> */}
                </Modal>
            );
        }
        return <></>;
    };
}

export default DobbiesStoreBasedNotifications;
